import { getRange } from '../../helpers/axisDef';
import {
  renderGrid,
  renderXAxisLabels,
  renderYaxisNumerical,
  renderVerticalStripDividers,
} from '../../zoompan-canvas/renderAxes';
import { getBoundData, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  createLogicalRange as createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange as createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTable } from '../../../../data/interfaces';
import { interpolateColors } from '../../helpers/colors';
import { getStandardDataColor } from '../../../themes/themes';
import { renderYLabel } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';
import { chartDims, convertColumnDataToDisplayString, generateChartTitle, isSelectedItem } from '../../helpers/misc';
import { getLabelSpaceSize } from '../../helpers/configParams';
import { currentTheme, getThemedGrayFraction } from '../../../themes/themes';
import { calculateKernelDF, createEmptyKernelDF, renderKernelDF, TpKernelDFData } from '../../helpers/kernelDF';
import {
  createColorMappingInfo,
  getMappedColorCategorical,
  TpColorDataMapperInfo,
} from '../../helpers/colorMappingInfo';
import { sortCategories, TpCategoryInfo } from '../../helpers/categoricalSorters';
import { TpDashboardGlobalChartConfig } from '../../dashboard/interfaces';

interface TpViolinCatInfo extends TpCategoryInfo {
  valueSum: number;
  valueAverage: number;
  positionIndex: number;
  values: number[];
  valuesSelected: number[];
  density: TpKernelDFData;
  densitySelected: TpKernelDFData;
}

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
  widgetGlobalConfig: TpDashboardGlobalChartConfig,
) {
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const dataTable: TpDataTable = boundData.dataTable;
  const dataCategories = boundData.bindings.categories.data;
  const dataValues = boundData.bindings.values.data;
  const dataItemId = boundData.bindings.itemId.data;
  const hoverLabelColInfo = boundData.bindings.hoverLabel?.columnInfo;
  const dataHoverLabel = boundData.bindings.hoverLabel?.data;
  const nameX = boundData.bindings.categories.columnInfo.name;
  const nameY = boundData.bindings.values.columnInfo.name;
  const title = generateChartTitle(dataContext, dataBinding, config, `${nameX} vs ${nameY}`);
  const colInfoCats = boundData.bindings.categories.columnInfo;

  const itemCount = dataValues.length;

  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;

  const categoriesMap: { [cat: string]: TpViolinCatInfo } = {};
  const categories: TpViolinCatInfo[] = [];
  for (let itemNr = 0; itemNr < itemCount; itemNr++) {
    const catValue = dataCategories[itemNr];
    if (!categoriesMap[catValue]) {
      const cat: TpViolinCatInfo = {
        value: catValue,
        displayString: convertColumnDataToDisplayString(colInfoCats, catValue),
        count: 0,
        valueSum: 0,
        valueAverage: 0,
        countSelected: 0,
        positionIndex: 0,
        values: [],
        valuesSelected: [],
        density: createEmptyKernelDF(),
        densitySelected: createEmptyKernelDF(),
      };
      categories.push(cat);
      categoriesMap[catValue] = cat;
    }
    const category = categoriesMap[catValue];
    const value = dataValues[itemNr];
    if (!isNaN(value)) {
      category.count++;
      category.valueSum += value;
      category.values.push(value);
      if (isSelectedItem(selectedItemsMap, dataItemId[itemNr])) category.valuesSelected.push(value);
    }
  }

  const [minVal, maxVal] = getRange(dataValues, 0.05);
  const kernelSize = (maxVal - minVal) * config.kernelSizeFactor ** 2 * 0.25;
  const gammaFactor = (config.densityGammaFactor / 10) ** 3 / 1 ** 3;

  let globalMaxDensity = 1.0e-99;
  for (const cat of categories) {
    cat.valueAverage = cat.valueSum / cat.count;
    cat.density = calculateKernelDF(cat.values, kernelSize, gammaFactor);
    cat.densitySelected = calculateKernelDF(cat.valuesSelected, kernelSize, gammaFactor);
    if (cat.density.maxDensity > globalMaxDensity) globalMaxDensity = cat.density.maxDensity;
  }

  const catCount = categories.length;
  sortCategories(categories, config.sortBy);
  if (config.sortBy == 'avgValInc') categories.sort((a, b) => a.valueAverage - b.valueAverage);
  if (config.sortBy == 'avgValDec') categories.sort((a, b) => b.valueAverage - a.valueAverage);

  const catLabels = categories.map((cat) => cat.displayString);

  let posIndex = 0;
  for (const cat of categories) {
    cat.positionIndex = posIndex;
    posIndex++;
  }

  let colorMappingInfo: TpColorDataMapperInfo | null = null;
  const globalColorMappingConfig = widgetGlobalConfig.colorMapping;
  if (config.colorType == 'categories')
    colorMappingInfo = createColorMappingInfo(config, boundData, 'categories', 1, globalColorMappingConfig);

  function renderColumnLabels(renderContext: TpAreaRenderContext) {
    renderXAxisLabels(renderContext, catLabels);
  }

  function renderStrips(renderContext: TpAreaRenderContext) {
    const ctx = renderContext.canvasContext;

    renderGrid(renderContext, false, true);
    renderVerticalStripDividers(categories.length, renderContext);

    const unSelectedStroke = getThemedGrayFraction(0.65);
    const unSelectedFill = getThemedGrayFraction(0.85);
    const selectedStroke = getStandardDataColor();
    const selectedFill = interpolateColors(getStandardDataColor(), currentTheme.colors.background, 0.5);
    ctx.lineWidth = renderContext.pixelRatio * 1;

    for (const cat of categories) {
      const stripPositionIndex = cat.positionIndex;

      renderKernelDF(
        renderContext,
        cat.density,
        globalMaxDensity,
        unSelectedStroke,
        unSelectedFill,
        (stripPositionIndex + 0.5) / catCount,
        0.9 / catCount,
        config.displayType,
        config.displayMean,
      );
      renderKernelDF(
        renderContext,
        cat.densitySelected,
        globalMaxDensity,
        colorMappingInfo ? getMappedColorCategorical(colorMappingInfo!, cat.value) : selectedStroke,
        colorMappingInfo
          ? interpolateColors(
              getMappedColorCategorical(colorMappingInfo!, cat.value),
              currentTheme.colors.background,
              0.5,
            )
          : selectedFill,
        (stripPositionIndex + 0.5) / catCount,
        0.9 / catCount,
        config.displayType,
        config.displayMean,
      );
    }
  }

  const hasLegend = false; // This is hard-coded...
  const legendBottomSize = hasLegend ? chartDims.legendHeight : 0;

  const padding = chartDims.padding;

  const xAxisHeight = getLabelSpaceSize(config.labelSpace);

  const zpLogicalRangeX = createLogicalRange(0, 1);
  const zpLogicalRangeY = createLogicalRange(minVal - kernelSize * 2, maxVal + kernelSize * 2);
  const zpRenderRangeX = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.yAxisWidth),
    RenderMaxDiffAbs(padding + 0),
  );
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.headerHeight),
    RenderMaxDiffAbs(padding + xAxisHeight + legendBottomSize),
  );

  const areaPoints: TpChartAreaDefinition = {
    id: 'strips',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    canAreaSelect: 'y',
    render: renderStrips,
  };

  const chartAreas: TpChartAreaDefinition[] = [
    areaPoints,
    {
      id: 'xaxis',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + xAxisHeight + legendBottomSize),
        RenderMaxDiffAbs(padding + legendBottomSize),
      ),
      zoomPanX: true,
      render: renderColumnLabels,
    },
    {
      id: 'yaxis',
      logicalRangeX: createLogicalRange(0, 1),
      logicalRangeY: zpLogicalRangeY,
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMinDiffAbs(padding + chartDims.yAxisWidth)), // offset of Y=4 to make axes overlap
      renderRangeY: zpRenderRangeY,
      zoomPanY: true,
      render: renderYaxisNumerical,
    },
    {
      id: 'ylabel',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + 0),
        RenderMinDiffAbs(padding + chartDims.headerHeight),
      ),
      render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, nameY, title),
    },
  ];

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
  };

  return {
    definition,
    areaPoints,
    dataTable,
    dataCategories,
    dataValues,
    hoverLabelColInfo,
    dataHoverLabel,
    catColumnId: boundData.bindings.categories.columnId,
    colInfoValues: boundData.bindings.values.columnInfo,
    colInfoCategories: boundData.bindings.categories.columnInfo,
    nameX,
    nameY,
    itemCount,
    dataItemId,
    strips: categories,
    stripMap: categoriesMap,
    getItemIndexById: boundData.getItemIndexById,
    title,
  };
}
